import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { auth } from '@/lib/auth';
import { useMe } from '@/lib/queries';
import { AuthLayout } from './auth-layout';
import { TotpDialog } from './settings/security';

const STEPS = ['install', 'scan', 'backup'] as const;

/**
 * Owners and admins need a second factor for billing management and every other privileged action
 * (`me.needs2fa`). New accounts get this step first, before the plan choice, instead of an error
 * when they open their invoices later. The gate disappears by itself once `me` reports the factor.
 */
export function SecureAccountGate() {
  const { t } = useTranslation();
  const me = useMe();
  const [open, setOpen] = useState(false);
  return (
    <AuthLayout title={t('secure.title')}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">{t('secure.subtitle')}</p>
        <ol className="flex flex-col gap-3">
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-start gap-3 text-sm">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-panel-2 text-xs font-medium">
                {i + 1}
              </span>
              {t(`secure.steps.${s}`)}
            </li>
          ))}
        </ol>
        <Button variant="primary" className="h-10 justify-center" onClick={() => setOpen(true)}>
          {t('secure.start')}
        </Button>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
          <span>{t('secure.signedInAs', { email: me.data?.user.email ?? '' })}</span>
          <span className="flex gap-3">
            <a href="/settings/security" className="hover:text-fg">
              {t('secure.passkey')}
            </a>
            <button
              type="button"
              className="hover:text-fg"
              onClick={async () => {
                await auth.signOut();
                window.location.assign('/login');
              }}
            >
              {t('billing.signOut')}
            </button>
          </span>
        </div>
      </div>
      {open && <TotpDialog mode="enable" onClose={() => setOpen(false)} />}
    </AuthLayout>
  );
}
